import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';

import {clinchEnums} from '@cl/shared';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {LocationCardScratchpadState} from '../../scratchpad/locationCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {CLEnumKeys, CLEnumType} from 'common/src/modules/enums/enums.types';
import {
  LocationTargetingMethods,
  LocationTargetingMethodValue,
} from 'common/src/modules/locationFeed/locationFeed.types';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';
import {DefaultMaxDistKm} from '../../../../schemas/locationCardSchema';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';

const strategiesWithLocationSet = [LocationTargetingMethodValue.NearestLocation];

const setLocationTargetingMethodToolSchema = z.object({
  targetingMethod: z.string().describe(
    `${describeIdFormat(IdPrefix.LocationStrategy)} Available options are: ${Object.values(
      LocationTargetingMethods,
    )
      .map(
        (method) =>
          `Name: ${method}, Value: ${LocationTargetingMethodValue[method as keyof typeof LocationTargetingMethodValue]}`,
      )
      .join(', ')}`,
  ),
});

export default function setLocationTargetingMethodTool() {
  async function execute(
    params: ToolExecuteParams<
      typeof setLocationTargetingMethodToolSchema,
      LocationCardScratchpadState
    >,
  ) {
    const campaignEnums = clinchEnums.loadedEnumsSubject.data[CLEnumType.CampaignPage];

    if (!campaignEnums) {
      return toolError('Campaign enums are not available');
    }

    const targetingMethodEnum = campaignEnums[CLEnumKeys.CampaignPage.LocationTargetingMethod];

    const {targetingMethod} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<LocationCardScratchpadState>(
      agentScratchpad,
      NodeType.STORE,
    );

    const parseResult = idHelper.parseLocationStrategyId(targetingMethod);

    if (parseResult.ok === false) {
      return toolError(formatParseError(parseResult.error));
    }

    const parsedTargetingMethod = parseResult.numericId;

    const targetingMethodEnumValue = targetingMethodEnum.byValue[parsedTargetingMethod];

    if (!targetingMethodEnumValue) {
      return toolError(
        `Invalid targeting method: ${targetingMethod}. Valid options are: ${Object.keys(
          targetingMethodEnum.byValue,
        )
          .map((id) => idHelper.toLocationStrategyId(+id))
          .join(', ')}`,
      );
    }

    if (!strategiesWithLocationSet.includes(parsedTargetingMethod)) {
      validatedScratchpad.card.locationSetId = undefined;
      validatedScratchpad.card.locationSet = undefined;
    }

    if (parsedTargetingMethod !== LocationTargetingMethodValue.SpecificStore) {
      validatedScratchpad.card.topLocationIds = [];
    }

    if (parsedTargetingMethod === LocationTargetingMethodValue.NearestLocation) {
      validatedScratchpad.card.maxDistKm = validatedScratchpad.card.maxDistKm || DefaultMaxDistKm;
    }

    validatedScratchpad.card.locationTargetingMethod = parsedTargetingMethod;

    return toolResult(
      `Location targeting method set to: ${idHelper.toLocationStrategyId(parsedTargetingMethod)}`,
    );
  }

  return tool({
    name: V2CardAgentToolName.SetLocationTargetingMethod,
    displayName: 'Set location targeting method',
    description:
      'Set the location targeting method for the location card (Nearest Location, By Zip Code, By DMA, Specific Store)',
    execute,
    inputSchema: setLocationTargetingMethodToolSchema,
  });
}
